<template>
  <div class="dashboard-shell">
    <AppTopbar />
    <main class="page-shell">
      <div class="container-readable stack-lg">
        
        <!-- Hero Banner עליון בעיצוב המותג -->
        <section class="hero-banner">
          <div class="hero-container">
            <div class="hero-content">
              <router-link to="/" class="back-link">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                  <path d="M19 12H5M12 19l-7-7 7-7"/>
                </svg>
                חזרה לאזור האישי
              </router-link>

              <h1 class="hero-title">חובות לתשלום</h1>
              <p class="hero-subtitle">פירוט חיובים חודשיים שטרם שולמו ואפשרות לסילוקם באופן מיידי.</p>
            </div>
          </div>
        </section>

        <!-- פאנל מרכזי -->
        <section class="surface card-block debts-panel stack-md">
          
          <!-- טעינה -->
          <div v-if="loadingDebts" class="loading-state muted">
            <div class="spinner"></div>
            <span>טוען את נתוני החובות...</span>
          </div>

          <!-- שגיאה בטעינה -->
          <div v-else-if="debtsError" class="alert-inline stack-sm">
            <p class="error-message">{{ debtsError }}</p>
            <button class="btn btn-ghost" @click="loadDebts">לנסות שוב</button>
          </div>

          <template v-else>
            <!-- הודעת הצלחה בתשלום -->
            <div v-if="paySuccess" class="pay-success-card">
              <div class="success-icon-wrap">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2.5"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>

              <span>{{ paySuccess }}</span>
            </div>

            <!-- הודעה כללית על התשלום -->
            <div v-if="payNotice" class="pay-notice-row">
              {{ payNotice }}
            </div>

            <!-- מצב שאין חובות -->
            <div v-if="!hasDebt" class="no-debt-card">
              <div class="no-debt-icon-wrap">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2.5"
                >
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
              </div>

              <div class="no-debt-info">
                <h3>אין חובות פתוחים!</h3>
                <p>כל החיובים בחשבונך שולמו כסדרם.</p>
              </div>
            </div>

            <!-- מצב שיש חובות -->
            <template v-else>
              <!-- כרטיס סיכום סה"כ לתשלום -->
              <div class="debt-total-card">
                <div class="debt-total-info">
                  <span class="debt-total-label">סה״כ לתשלום</span>
                  <span class="debt-total-amount">
                    {{ formatAmount(totalDebt) }} ₪
                  </span>
                </div>

                <button
                  class="btn btn-primary pay-btn-top"
                  :disabled="paying"
                  @click="payDebts"
                >
                  <span v-if="paying" class="spinner-sm"></span>
                  <span>
                    {{ paying ? 'מבצע חיוב...' : 'תשלום עכשיו' }}
                  </span>
                </button>
              </div>

              <!-- רשימת חודשי החוב -->
              <div class="debt-months-section stack-sm">
                <h2 class="section-title">פירוט החיובים</h2>

                <div class="debt-months-list">
                  <div
                    v-for="row in debts"
                    :key="row.year + '-' + row.month"
                    class="debt-month-card"
                  >
                    <div class="debt-row-main">
                      <div class="debt-month-head">
                        <span class="debt-month-name">
                          {{ monthName(row.month) }} {{ row.year }}
                        </span>

                        <span
                          v-if="row.count > 1"
                          class="debt-count-badge"
                        >
                          {{ row.count }} חיובים
                        </span>
                      </div>

                      <span
                        v-if="row.plan_name"
                        class="debt-plan-name"
                      >
                        {{ row.plan_name }}
                      </span>
                    </div>

                    <span class="debt-month-amount">
                      {{ formatAmount(row.amount) }} ₪
                    </span>
                  </div>
                </div>
              </div>

              <!-- הודעת שגיאה בתשלום -->
              <div v-if="payError" class="alert-inline error-alert">
                <p class="error-message pay-error">
                  {{ payError }}
                </p>
              </div>
            </template>
          </template>
</section>

      </div>
    </main>
    <AppFooter />
  </div>
</template>

<script>
import AppFooter from '@/components/AppFooter.vue'
import AppTopbar from '@/components/AppTopbar.vue'

const MONTH_NAMES = [
  'ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני',
  'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר'
]

const NO_TOKEN_MSG = 'לא נמצא אמצעי תשלום שמור. יש להוסיף כרטיס לפני התשלום.'

export default {
  name: 'Debts',
  components: { AppTopbar, AppFooter },
  data() {
    return {
      loadingDebts: true,
      debtsError: '',
      hasDebt: false,
      totalDebt: 0,
      debts: [],
      paying: false,
      payError: '',
      paySuccess: '',
      payNotice: '',
      noTokenMsg: NO_TOKEN_MSG,
    }
  },
  mounted() {
    if (this.isNull(localStorage.getItem('user_token'))) { this.$router.push('/login'); return }
    const msg = this.$route?.query?.success
    const notice = this.$route?.query?.info
    if (msg) {
      this.paySuccess = decodeURIComponent(msg)
      this.$router.replace({ path: '/debts' })
    } else if (notice) {
      this.payNotice = decodeURIComponent(notice)
      this.$router.replace({ path: '/debts' })
    }
    this.loadDebts()
  },
  methods: {
    loadDebts() {
      this.loadingDebts = true; this.debtsError = ''
      this.api({ action: 'get_my_debts', method: 'get' }, (response) => {
        if (response?.data?.success) {
          this.hasDebt = !!response.data.has_debt
          this.totalDebt = response.data.total_debt || 0
          this.debts = response.data.debts || []
        } else {
          this.debtsError = 'אירעה שגיאה בטעינת נתוני החובות.'
        }
        this.loadingDebts = false
      }, () => { this.debtsError = 'אירעה שגיאה זמנית. נסו שוב.'; this.loadingDebts = false })
    },
    payDebts() {
      this.paying = true; this.payError = ''; this.paySuccess = ''
      this.api({ action: 'pay_my_debts', method: 'post' }, (response) => {
        this.paying = false
        const data = response?.data
        if (data?.success) {
          this.paySuccess = data.already_paid
            ? 'החוב כבר שולם.'
            : `התשלום בוצע בהצלחה — חויב סך ${this.formatAmount(data.total_paid)} ₪ מאמצעי התשלום השמור.`
          this.loadDebts()
        } else if (data?.error === 'no_payment_token') {
          this.$router.push({ path: '/payment-method', query: { error: encodeURIComponent(NO_TOKEN_MSG) } })
        } else {
          this.payError = data?.msg || 'התשלום נכשל. נסו שוב או פנו לתמיכה.'
        }
      }, () => { this.paying = false; this.payError = 'אירעה שגיאה זמנית. נסו שוב.' })
    },
    monthName(m) {
      return MONTH_NAMES[(m - 1 + 12) % 12] || ''
    },
    formatAmount(val) {
      return this.format_number((Math.round(Number(val) * 100) / 100).toString())
    },
  }
}
</script>

<style lang="scss" scoped>
@use "@/styles/tokens" as *;

// --- הבאנר העליון (Hero) ---
.hero-banner {
  background: linear-gradient(135deg, $color-brand 0%, lighten($color-brand, 10%) 100%);
  color: #ffffff;
  border-radius: $radius-lg;
  padding: $space-5 $space-6;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.06);
}

.hero-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: $space-4;
  flex-wrap: wrap;
}

.hero-content {
  display: flex;
  flex-direction: column;
  gap: $space-2;
  max-width: 600px;
}

.back-link {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  color: rgba(255, 255, 255, 0.9);
  text-decoration: none;
  font-weight: 500;
  font-size: 0.88rem;
  margin-bottom: $space-1;
  transition: opacity $transition-fast;

  &:hover {
    opacity: 0.8;
    text-decoration: underline;
  }

  svg {
    transform: rotate(180deg);
  }
}

.hero-title {
  margin: 0;
  font-size: 1.8rem;
  font-weight: 700;
  color: #ffffff;
  line-height: 1.2;
}

.hero-subtitle {
  margin: 0;
  color: rgba(255, 255, 255, 0.88);
  font-size: 1rem;
}

// --- פאנל מרכזי ---
.debts-panel {
  border: 1px solid $color-line;
  border-radius: $radius-md;
  padding: $space-5;
}

// --- מצב אין חובות ---
.no-debt-card {
  display: flex;
  align-items: center;
  gap: $space-4;
  padding: $space-5;
  background: #f0fdf4;
  border: 1.5px solid #bbf7d0;
  border-radius: $radius-md;
  color: #166534;

  .no-debt-icon-wrap {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    background: #dcfce7;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    color: #16a34a;
  }

  .no-debt-info {
    h3 {
      margin: 0 0 0.2rem 0;
      font-size: 1.15rem;
      font-weight: 700;
      color: #14532d;
    }
    p {
      margin: 0;
      font-size: 0.92rem;
      color: #166534;
    }
  }
}

// --- כרטיס סה"כ לתשלום ---
.debt-total-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: $space-4;
  padding: $space-4 $space-5;
  background: #fff5f5;
  border: 1.5px solid #fecdd3;
  border-radius: $radius-md;
  flex-wrap: wrap;
}

.debt-total-info {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.debt-total-label {
  font-size: 0.85rem;
  font-weight: 600;
  color: #9f1239;
}

.debt-total-amount {
  font-size: 1.6rem;
  font-weight: 800;
  color: #881337;
}

.pay-btn-top {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  white-space: nowrap;
}

// --- פירוט חיובים ---
.debt-months-section {
  margin-top: $space-3;
}

.section-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: $color-text;
  margin: 0 0 $space-2;
}

.debt-months-list {
  display: flex;
  flex-direction: column;
  gap: $space-2;
}

.debt-month-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: $space-3 $space-4;
  background: #fdfbff;
  border: 1px solid $color-line;
  border-radius: $radius-sm;
  transition: border-color $transition-fast;

  &:hover {
    border-color: lighten($color-brand, 25%);
  }
}

.debt-row-main {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.debt-month-head {
  display: flex;
  align-items: center;
  gap: $space-2;
}

.debt-month-name {
  font-weight: 700;
  color: $color-text;
  font-size: 0.95rem;
}

.debt-count-badge {
  font-size: 0.75rem;
  font-weight: 600;
  background: #f0eafc;
  color: $color-brand;
  padding: 0.1rem 0.5rem;
  border-radius: 999px;
}

.debt-plan-name {
  font-size: 0.82rem;
  color: $color-text-muted;
}

.debt-month-amount {
  font-size: 1.05rem;
  font-weight: 700;
  color: #9f2f50;
}

// --- הודעת הצלחה ---
.pay-success-card {
  display: flex;
  align-items: center;
  gap: $space-3;
  padding: $space-4 $space-5;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: $radius-md;
  color: #166534;
  font-weight: 600;
  font-size: 0.95rem;

  .success-icon-wrap {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: #dcfce7;
    color: #16a34a;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
}

// --- אזור תשלום / iFrame ---
.payment-window-section {
  margin-top: $space-4;
  padding-top: $space-4;
  border-top: 1px dashed $color-line;
}

.save-card-option {
  display: flex;
  align-items: center;
}

.custom-checkbox {
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  cursor: pointer;
  user-select: none;
  font-size: 0.92rem;
  font-weight: 500;
  color: $color-text;

  input {
    display: none;

    &:checked + .checkmark {
      background-color: $color-brand;
      border-color: $color-brand;

      &::after {
        display: block;
      }
    }
  }

  .checkmark {
    width: 20px;
    height: 20px;
    border: 2px solid $color-line;
    border-radius: 4px;
    background-color: #ffffff;
    position: relative;
    transition: all $transition-fast;

    &::after {
      content: "";
      position: absolute;
      display: none;
      left: 6px;
      top: 2px;
      width: 5px;
      height: 10px;
      border: solid white;
      border-width: 0 2px 2px 0;
      transform: rotate(45deg);
    }
  }
}

.pay-btn-iframe {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-top: $space-2;
}

.iframe-wrap {
  margin-top: $space-3;
  border: 1.5px solid $color-line;
  border-radius: $radius-md;
  overflow: hidden;
  background: #ffffff;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.03);
}

.payment-iframe {
  width: 100%;
  min-height: 520px;
  display: block;
  border: none;
  }
.pay-notice-row {
  background: #fff7e6; color: #8a5a00; border: 1px solid #f0dcae; border-radius: 10px;
  padding: 0.85rem 1rem; margin-bottom: 1rem; font-weight: 650;
}

// --- הודעות שגיאה וספינר ---
.alert-inline {
  border: 1px solid #f0bfd0;
  background: #fff4f8;
  border-radius: $radius-sm;
  padding: $space-4;
  text-align: center;

  .error-message {
    margin: 0;
    color: $color-danger;
    font-weight: 600;
    font-size: 0.9rem;
  }
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: $space-3;
  padding: $space-6 0;
}

.spinner {
  width: 32px;
  height: 32px;
  border: 3px solid rgba($color-brand, 0.15);
  border-top-color: $color-brand;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.spinner-sm {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #ffffff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  display: inline-block;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

// --- רספונסיביות ---
@media (max-width: 680px) {
  .hero-banner {
    padding: $space-4;
  }

  .debt-total-card {
    flex-direction: column;
    align-items: flex-start;
  }

  .pay-btn-top {
    width: 100%;
    justify-content: center;
  }

  .payment-iframe {
    min-height: 480px;
  }
}
</style>